import { formatCurrency, resolveMediaUrl } from "./utils";
import { openHtmlDocument } from "./docViewer";

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const SAFE_IMG = /^(https?:\/\/|\/|data:image\/(png|jpe?g|gif|webp);base64,)/i;
const safeImg = (u) => (u && SAFE_IMG.test(String(u).trim()) ? esc(resolveMediaUrl(String(u).trim())) : "");

export const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const label = new Date(y, (m || 1) - 1, 1).toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const payslipLines = (r) => {
  const earnings = r.earnings?.length ? r.earnings : r.bonus > 0 ? [{ label: "Bónus / Subsídios", amount: r.bonus }] : [];
  const deductions = r.deductions_items?.length ? r.deductions_items : r.deductions > 0 ? [{ label: "Descontos", amount: r.deductions }] : [];
  const gross = r.gross_salary ?? (r.base_salary || 0) + earnings.reduce((a, e) => a + (e.amount || 0), 0);
  const totalDeductions = r.total_deductions ?? deductions.reduce((a, d) => a + (d.amount || 0), 0);
  return { earnings, deductions, gross, totalDeductions };
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-PT") : "—");

const payslipBody = ({ record: r, settings = {}, employee = {} }) => {
  const clean = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v !== null && v !== undefined && v !== ""));
  const emp = { ...clean(employee), ...clean(r.employee) };
  const { earnings, deductions, gross, totalDeductions } = payslipLines(r);
  const logoRaw = settings.logo_url || settings.logo_file || "";
  const logoSrc = safeImg(logoRaw);
  const er = r.employer || {};
  const company = er.name || settings.restaurant_name || settings.business_name || "Entidade Empregadora";
  const employerNif = er.nif || settings.nif || "";
  const addressLine = [er.address || settings.address, er.city || settings.city, settings.country === "AO" || !settings.country ? "Angola" : settings.country].filter(Boolean).join(" · ");
  const phone = er.phone || settings.phone || "";
  const email = er.email || settings.email || "";
  const rows = Math.max(earnings.length + 1, deductions.length, 1);
  const line = (l, neg) => `<tr><td>${esc(l.label)}</td><td class="r mono">${neg ? "−" : ""}${formatCurrency(l.amount)}</td></tr>`;
  const earnRows = [{ label: "Salário base", amount: r.base_salary || 0 }, ...earnings];
  const pad = (arr, n) => arr + Array.from({ length: Math.max(0, n) }, () => '<tr class="pad"><td>&nbsp;</td><td></td></tr>').join("");
  const nameFull = emp.full_name || r.employee_name;

  return `<section class="sheet">
<div class="hd">
  <div class="co">
    ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="Logo" onerror="this.style.display='none'"/>` : ""}
    <div>
      <h1>${esc(company)}</h1>
      ${employerNif ? `<p><b>NIF:</b> ${esc(employerNif)}</p>` : ""}
      ${addressLine ? `<p>${esc(addressLine)}</p>` : ""}
      ${phone || email ? `<p>${phone ? `<b>Tel:</b> ${esc(phone)}` : ""}${phone && email ? " · " : ""}${email ? `<b>Email:</b> ${esc(email)}` : ""}</p>` : ""}
    </div>
  </div>
  <div class="doc">
    <h2>RECIBO DE VENCIMENTO</h2>
    ${r.receipt_number ? `<div class="num">Nº ${esc(r.receipt_number)}</div>` : ""}
    <p>Período: <b>${esc(monthLabel(r.month))}</b></p>
    <p>Emitido em ${fmtDate(r.created_at || new Date().toISOString())}</p>
  </div>
</div>

<div class="blocks">
  <div class="blk">
    <h3>Entidade Empregadora</h3>
    <p><span>Designação</span><b>${esc(company)}</b></p>
    <p><span>NIF</span><b class="${employerNif ? "" : "miss"}">${esc(employerNif || "NÃO CONFIGURADO")}</b></p>
    ${addressLine ? `<p><span>Morada</span><b>${esc(addressLine)}</b></p>` : ""}
  </div>
  <div class="blk">
    <h3>Trabalhador</h3>
    <p><span>Nome completo</span><b>${esc(nameFull)}</b></p>
    <p><span>Cargo / Função</span><b>${esc(emp.position || emp.role || "—")}</b></p>
    ${emp.department ? `<p><span>Departamento</span><b>${esc(emp.department)}</b></p>` : ""}
    <p><span>Nº Beneficiário INSS</span><b class="${emp.inss_number ? "" : "miss"}">${esc(emp.inss_number || "NÃO REGISTADO")}</b></p>
    <p><span>NIF</span><b>${esc(emp.nif || "—")}</b></p>
    ${emp.hire_date ? `<p><span>Data de admissão</span><b>${fmtDate(emp.hire_date)}</b></p>` : ""}
  </div>
</div>

<div class="grid">
  <table>
    <thead><tr><th>Remunerações</th><th class="r">Valor</th></tr></thead>
    <tbody>${pad(earnRows.map((l) => line(l, false)).join(""), rows - earnRows.length)}
    <tr class="sub"><td>Total Remunerações (Bruto)</td><td class="r mono">${formatCurrency(gross)}</td></tr></tbody>
  </table>
  <table>
    <thead><tr><th>Descontos e Deduções</th><th class="r">Valor</th></tr></thead>
    <tbody>${pad(deductions.length ? deductions.map((l) => line(l, true)).join("") : '<tr><td colspan="2" class="c" style="color:#777">Sem descontos</td></tr>', rows - Math.max(deductions.length, 1))}
    <tr class="sub"><td>Total Descontos</td><td class="r mono">−${formatCurrency(totalDeductions)}</td></tr></tbody>
  </table>
</div>

<div class="tot">
  <div class="lbl">VALOR LÍQUIDO PAGO<small>Total que o trabalhador efetivamente recebe</small></div>
  <div class="val mono">${formatCurrency(r.net_salary)}</div>
</div>

<div class="pay">
  <div><span>Estado:</span> <span class="st ${r.status === "paid" ? "paid" : "pend"}">${r.status === "paid" ? "PAGO" : "PENDENTE"}</span></div>
  ${r.paid_at ? `<div><span>Data de pagamento:</span> <b>${fmtDate(r.paid_at)}</b></div>` : ""}
  ${emp.bank_iban ? `<div><span>Transferência para IBAN:</span> <b class="mono">${esc(emp.bank_iban)}</b></div>` : ""}
  ${r.created_by_name ? `<div><span>Processado por:</span> <b>${esc(r.created_by_name)}</b></div>` : ""}
</div>

${r.notes ? `<div class="note"><b>Observações</b>${esc(r.notes)}</div>` : ""}

<div class="sig">
  <div>A Entidade Empregadora<br/><br/>${esc(company)}</div>
  <div>O Trabalhador<br/><br/>${esc(nameFull)}</div>
</div>

<div class="ft">Documento processado por computador · ${r.receipt_number ? `Recibo ${esc(r.receipt_number)} · ` : ""}${esc(company)}${employerNif ? ` · NIF ${esc(employerNif)}` : ""} · Remunerações em Kwanzas (AOA)</div>
</section>`;
};

const PAYSLIP_CSS = `@page{size:A4;margin:14mm}
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;font-size:11.5px;line-height:1.45}
@media screen{body{max-width:190mm;margin:16px auto;padding:0 8px}}
.mono{font-family:"Courier New",monospace}.r{text-align:right}.c{text-align:center}
.hd{display:flex;justify-content:space-between;gap:24px;border-bottom:3px solid #111;padding-bottom:12px}
.co{display:flex;gap:14px;align-items:flex-start;max-width:60%}
.logo{width:76px;height:76px;object-fit:contain;border:1px solid #ddd;border-radius:8px;padding:4px;background:#fff}
.co h1{font-size:18px;margin:0 0 4px;font-weight:800}.co p{margin:1px 0;font-size:11px;color:#333}
.doc{text-align:right}.doc h2{margin:0;font-size:18px;letter-spacing:1.5px;font-weight:800}.doc p{margin:2px 0;font-size:11px;color:#333}
.num{display:inline-block;background:#111;color:#fff;font-weight:800;padding:3px 10px;border-radius:4px;margin:4px 0;font-size:12px;letter-spacing:.5px}
.blocks{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px}
.blk{border:1px solid #ccc;border-radius:6px;padding:10px 12px}
.blk h3{margin:0 0 6px;font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#555;border-bottom:1px solid #ddd;padding-bottom:4px}
.blk p{margin:3px 0;display:flex;justify-content:space-between;gap:10px}.blk p span:first-child{color:#555}.blk p b{text-align:right}
.miss{color:#c0392b;font-weight:700}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px;align-items:start}
table{width:100%;border-collapse:collapse}
th{border-top:2px solid #111;border-bottom:2px solid #111;padding:6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px;background:#f4f4f4}
td{padding:5px 6px;border-bottom:1px solid #e3e3e3}
tr.pad td{border-bottom:1px solid #f3f3f3;color:transparent}
tr.sub td{border-top:2px solid #111;border-bottom:none;font-weight:800;background:#fafafa}
.tot{margin-top:16px;border:2px solid #111;border-radius:6px;padding:10px 14px;display:flex;justify-content:space-between;align-items:center}
.tot .lbl{font-size:12px;font-weight:800;letter-spacing:1px}.tot .lbl small{display:block;font-weight:400;letter-spacing:0;color:#555;font-size:10px}
.tot .val{font-size:22px;font-weight:900}
.pay{margin-top:12px;display:flex;gap:18px;flex-wrap:wrap;font-size:11px}.pay span{color:#555}
.st{display:inline-block;padding:2px 8px;border-radius:3px;font-weight:800;font-size:10px}
.st.paid{background:#16a34a;color:#fff}.st.pend{background:#f59e0b;color:#111}
.note{margin-top:12px;border:1px solid #bbb;border-radius:6px;padding:8px 12px;font-size:11px}.note b{display:block;font-size:10px;text-transform:uppercase;color:#555;margin-bottom:3px}
.sig{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:46px}
.sig div{border-top:1px solid #111;padding-top:6px;text-align:center;font-size:10.5px;color:#333}
.ft{margin-top:22px;border-top:1px solid #bbb;padding-top:8px;text-align:center;font-size:9.5px;color:#555}
.sheet{page-break-after:always;break-after:page}.sheet:last-child{page-break-after:auto;break-after:auto}
@media screen{.sheet+.sheet{margin-top:24px;border-top:2px dashed #bbb;padding-top:24px}}
`;

const wrapDocument = (title, bodies) => `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${PAYSLIP_CSS}</style></head><body>
${bodies.join("\n")}
<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
</body></html>`;

export const buildPayslipHtml = (args) =>
  wrapDocument(`Recibo de Vencimento ${args.record.receipt_number || args.record.month} — ${args.record.employee_name || ""}`, [payslipBody(args)]);

export const buildPayslipsBatchHtml = ({ records, settings = {}, employeesById = {} }) =>
  wrapDocument(`Recibos de Vencimento ${monthLabel(records[0]?.month)} (${records.length})`,
    records.map((r) => payslipBody({ record: r, settings, employee: employeesById[r.user_id] || {} })));

const openDoc = (html) => openHtmlDocument(html);

export const printPayslip = (args) => openDoc(buildPayslipHtml(args));
export const printPayslipsBatch = (args) => (args.records?.length ? openDoc(buildPayslipsBatchHtml(args)) : false);
